/** API 存取層。
 *
 *  行為刻意與原生版 app.js 的 api() 完全一致：
 *    - cookie 同源認證（伺服器設 HttpOnly session，30 天不登出靠後端 cookie 效期）
 *    - 401 代表 session 失效 → 清狀態並導回登入頁；但登入端點自己處理 401
 *    - 錯誤訊息優先取後端的 detail 欄位，否則退回 HTTP 狀態碼
 *    - 204 回傳 null（刪除類端點沒有內容）
 */

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

/** session 失效時通知外層（由 AuthProvider 註冊）。 */
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: () => void) {
  onUnauthorized = fn;
}

export async function api<T = unknown>(
  path: string,
  opts: RequestInit = {},
): Promise<T> {
  const res = await fetch(path, {
    credentials: 'same-origin',
    headers:
      opts.body instanceof FormData
        ? undefined
        : { 'Content-Type': 'application/json' },
    ...opts,
  });

  if (res.status === 401) {
    if (!path.endsWith('/auth/login')) {
      onUnauthorized?.();
    }
    throw new ApiError('未登入或登入已過期', 401);
  }

  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      if (body?.detail) detail = body.detail;
    } catch {
      /* 後端沒回 JSON 就沿用狀態碼訊息 */
    }
    throw new ApiError(detail, res.status);
  }

  return (res.status === 204 ? null : await res.json()) as T;
}

/* ── 便利包裝 ───────────────────────────────────── */

export const apiGet = <T,>(path: string) => api<T>(path);

export const apiPost = <T,>(path: string, body?: unknown) =>
  api<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined });

export const apiPatch = <T,>(path: string, body?: unknown) =>
  api<T>(path, { method: 'PATCH', body: body ? JSON.stringify(body) : undefined });

export const apiPut = <T,>(path: string, body?: unknown) =>
  api<T>(path, { method: 'PUT', body: body ? JSON.stringify(body) : undefined });

export const apiDelete = <T,>(path: string) => api<T>(path, { method: 'DELETE' });
